'use strict';

import React from 'react';
import { useState, useEffect, useRef } from 'react';
import { Button, Row, Col, Form, Stack } from 'react-bootstrap';

import { Formik } from 'formik';
import * as yup from 'yup';

import useRequest from '../helper/useRequest';
import { toast } from 'react-toastify';
import Alert from '../helper/Alert';
import ModalDefault from '../helper/Modal/Modal';

import Select from 'react-select';

import { date, timeStart, timeEnd, grade, nameClass } from '../helper/Schema';

function validateTimes(start, end, minDiff) {
    let s = {};
    let e = {};
    if (start.length) { s = new Date("1970-01-01 " + start); }
    else return false;
    if (end.length) { e = new Date("1970-01-01 " + end); }
    else return false;

    let diff = e.getTime() - s.getTime();
    let check = 60000 * minDiff;
    return diff >= check;
}

const TimeslotEdit = ({ timeslot, open, setOpen, editTimeslot }) => {
    const { request } = useRequest();

    const [message, setMessage] = useState(false);
    const [loading, setLoading] = useState(false);
    const [locked, setLocked] = useState(false);
    const [equipment, setEquipment] = useState([]);
    const [categories, setCategories] = useState([]);
    const [contactPersons, setContactPersons] = useState([]);
    const [businessContactPersons, setBusinessContactPersons] = useState([]);
    const [sndBusinessContactPersons, setSndBusinessContactPersons] = useState([]);
    const [disabled, setDisabled] = useState(false);

    const formRef = useRef();

    const schema = {
        appointment: date.test("checkDate", "Es kann kein Termin an einem Sonntag stattfinden.", (value) => { return value.getDay() > 0; }),
        timeStart: timeStart,
        timeEnd: timeEnd.test("checkEndTime", "Die Endzeit muss mindestens 30 Minuten nach der Startzeit liegen.", (value) => { return validateTimes(formRef.current.values.timeStart, value, 30); }),
        grade: grade,
        nameClass: nameClass
    }

    const footer = <Button disabled={loading || locked} variant="primary" onClick={() => formRef.current.submitForm()}>Speichern</Button>;

    const setOpen2 = (s) => {
        if (loading) return;

        setOpen(s);
    }

    const updateTimeslot = async (values) => {
        let data = {
            TimeslotID: timeslot.TimeslotID,
            Appointment: values.appointment,
            TimeStart: values.timeStart,
            TimeEnd: values.timeEnd,
            Grade: values.grade,
            Class: values.nameClass,
            Room: null,
            ExpireTime: values.expiration,
            Equipment: values.equipment.map(({ value }) => (parseInt(value))),
            Categories: values.categories.map(({ value }) => (parseInt(value))),
            ContactPersonSchool: values.contactPerson.value,
            ContactPersonOrganisation: values.contactBusiness.value,
            SndContactPersonOrganisation: values.sndContactBusiness.value
        };

        if (values.room) {
            data.Room = values.roomName;
        }

        try {
            if (values.contactBusiness.value != null && values.contactBusiness.value != 0 && values.contactBusiness.value == values.sndContactBusiness.value) {
                toast.error("Als Zweitansprechpartner darf nicht die selbe Person wie beim ersten Ansprechpartner ausgewählt werden.");
            }
            else {
                if (data.Expiration < 0) {
                    toast.error("Die eingegebene Ablaufzeit (in Stunden vor Termin-Begin) ist negativ. Bitte ändern Sie Ihre Eingabe auf eine positive Ganzzahl.");
                }
                else {
                    setMessage(false);
                    setLoading(true);
                    let result = await request("/Operator/EditTimeslot", data);
                    setLoading(false);
                    let json = result.data;
                    if (json.r != 1) {
                        if (json.message) {
                            setMessage(json.message);
                        }
                        else {
                            toast.error("Es ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.");
                        }
                    }
                    else {
                        toast.success("Termin erfolgreich gespeichert.");
                        editTimeslot(json.timeslot);
                        setOpen(false);
                    }
                }
            }     
        }
        catch {
            setLoading(false);
            toast.error("Es ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.");
        }
    }

    const getSlotOptions = async (timeslotId) => {
        try {
            let result = await request("/Operator/getSlotOptions", { timeslotId });
            let json = result.data;
            if (json.r != 1) {
                if (json.message) {
                    aler.error(json.message);
                }
                else {
                    toast.error("Es ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut");
                }
            }
            else {
                let persons = json.contactPerson.map((val) => {
                    if (val.DefaultPerson == 1) {
                        return { value: 0, label: val.FirstName + " " + val.LastName }
                    }
                    return { value: val.ContactPersonID, label: val.FirstName + " " + val.LastName }
                });

                let businessPersons = json.contactBusiness.map((val) => {
                    if (val.DefaultPerson == 1) {
                        return { value: 0, label: val.FirstName + " " + val.LastName }
                    }
                    return { value: val.ContactPersonID, label: val.FirstName + " " + val.LastName }
                });

                let sndBusinessPersons = json.contactBusiness.map((val) => {
                    return { value: val.ContactPersonID, label: val.FirstName + " " + val.LastName }
                });
                sndBusinessPersons.splice(0, 0, { value: 0, label: "Kein Zweitreferent" });

                setEquipment(json.equipment.map((val) => { return { value: val.EquipmentID, label: val.Description } }));
                setCategories(json.categories.map((val) => { return { value: val.CategoryID, label: val.Description } }));
                setContactPersons(persons);
                setBusinessContactPersons(businessPersons);
                setSndBusinessContactPersons(sndBusinessPersons);
            }
        }
        catch (e) {
            toast.error("Fehler beim Laden.");
        }
    }

    useEffect(() => {
        if (!formRef.current) return;

        if (timeslot.Status != 1) {
            setDisabled(true);
        }
        else {
            setDisabled(false);
        }

        formRef.current.setFieldValue("appointment", timeslot.Appointment);
        formRef.current.setFieldValue("timeStart", timeslot.TimeStart.substr(0, 5));
        formRef.current.setFieldValue("timeEnd", timeslot.TimeEnd.substr(0, 5));
        formRef.current.setFieldValue("grade", timeslot.Grade);
        formRef.current.setFieldValue("nameClass", timeslot.Class);
        if (timeslot.Room) {
            formRef.current.setFieldValue("room", true);
            formRef.current.setFieldValue("roomName", timeslot.Room);
        }
        else {
            formRef.current.setFieldValue("room", false);
        }

        formRef.current.setFieldValue("equipment", timeslot.Equipment.map((val) => { return { value: val.EquipmentID, label: val.Description } }));
        if (timeslot.Category) {
            formRef.current.setFieldValue("categories", timeslot.Category.map((val) => { return { value: val.CategoryID, label: val.Description } }));
        }
        else {
            formRef.current.setFieldValue("categories", []);
        }
        
        formRef.current.setFieldValue("expiration", timeslot.ExpireTime);
        
        if (timeslot.ContactSchool) {
            formRef.current.setFieldValue("contactPerson", { value: timeslot.ContactSchool.ContactPersonID, label: timeslot.ContactSchool.FirstName + " " + timeslot.ContactSchool.LastName });
        }  

        if (timeslot.ContactOrganisation) {
            formRef.current.setFieldValue("contactBusiness", { value: timeslot.ContactOrganisation.ContactPersonID, label: timeslot.ContactOrganisation.FirstName + " " + timeslot.ContactOrganisation.LastName });
            if (timeslot.SndContactOrganisation.ContactPersonID > 0) {
                formRef.current.setFieldValue("sndContactBusiness", { value: timeslot.SndContactOrganisation.ContactPersonID, label: timeslot.SndContactOrganisation.FirstName + " " + timeslot.SndContactOrganisation.LastName });
            }
        }

        let d = new Date(timeslot.Appointment + " " + timeslot.TimeStart);
        if (d < new Date()) {
            setLocked(true);
            return;
        }
        else {
            setLocked(false);
        }

        getSlotOptions(timeslot.TimeslotID);
    }, [timeslot]);

    /*
    useEffect(() => {
        const getSettings = async () => {
            try {
                let path = location.pathname.split("/");
                if (path.length < 4) return;

                let organisationId = path[3];
                let result = await request("/Operator/GetOrganisationSettings", {organisationId});
                let json = result.data;
                if (json.r != 1) {
                    if (json.message) {
                        aler.error(json.message);
                    }
                    else {
                        toast.error("Es ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut");
                    }
                }
                else {
                    let defaultPerson = false;
                    let persons = json.contactPerson.map((val) => {
                        if (val.DefaultPerson == 1) {
                            defaultPerson = val;
                            return { value: 0, label: val.FirstName + " " + val.LastName }
                        }
                        return { value: val.ContactPersonID, label: val.FirstName + " " + val.LastName }
                    });


                    setEquipment(json.equipment.map((val) => { return { value: val.EquipmentID, label: val.Description } }));
                    setCategories(json.categories.map((val) => { return { value: val.CategoryID, label: val.Description } }));
                    setContactPersons(persons);

                }
            }
            catch (e) {
                toast.error("Fehler beim Laden.");
            }
        }
        getSettings();
    }, []);
    */

    return (
        <ModalDefault open={open} setOpen={setOpen2} title="Termin bearbeiten" footer={footer}>
            {message && <Alert type="error" message={message} />}

            <Formik
                innerRef={(f) => { formRef.current = f; }}
                validationSchema={yup.object().shape(schema)}
                onSubmit={(values) => {
                    updateTimeslot(values);
                }}
                initialValues={{
                    appointment: '',
                    timeStart: '',
                    timeEnd: '',
                    grade: 5,
                    nameClass: '',
                    room: false,
                    roomName: '',
                    equipment: [],
                    categories: [],
                    expiration: 24,
                    contactPerson: '',
                    contactBusiness: '',
                    sndContactBusiness: ''
                }}
            >
                {({
                    handleSubmit,
                    handleChange,
                    handleBlur,
                    values,
                    touched,
                    isValid,
                    errors,
                }) => {

                    return (
                        <Form noValidate onSubmit={handleSubmit}>
                            <Stack gap={2}>
                                <Form.Group controlId="appointment">
                                    <Form.Label>Datum</Form.Label>
                                    <Form.Control disabled={disabled || loading || locked} isInvalid={errors.appointment && touched.appointment} type="date" placeholder="" name="appointment" value={values.appointment} onBlur={handleBlur} onChange={handleChange} />
                                    <Form.Control.Feedback type="invalid">
                                        {errors.appointment}
                                    </Form.Control.Feedback>
                                </Form.Group>

                                <Row>
                                    <Col>
                                        <Form.Group controlId="timeStart">
                                            <Form.Label>Anfangszeit</Form.Label>
                                            <Form.Control disabled={disabled || loading || locked} isInvalid={errors.timeStart && touched.timeStart} type="time" placeholder="" name="timeStart" value={values.timeStart} onBlur={handleBlur} onChange={(e) => { handleChange(e); }} />
                                            <Form.Control.Feedback type="invalid">
                                                {errors.timeStart}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group controlId="timeEnd">
                                            <Form.Label>Endzeit</Form.Label>
                                            <Form.Control disabled={disabled || loading || locked} isInvalid={errors.timeEnd && touched.timeEnd} type="time" placeholder="" name="timeEnd" value={values.timeEnd} onBlur={handleBlur} onChange={handleChange} />
                                            <Form.Control.Feedback type="invalid">
                                                {errors.timeEnd}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Row>
                                    <Col>
                                        <Form.Group controlId="grade">
                                            <Form.Label>Stufe</Form.Label>
                                            <Form.Control disabled={loading || locked} isInvalid={errors.grade && touched.grade} type="number" step="1" min="5" max="13" placeholder="" name="grade" value={values.grade} onBlur={handleBlur} onChange={handleChange} />
                                            <Form.Control.Feedback type="invalid">
                                                {errors.grade}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group controlId="nameClass">
                                            <Form.Label>Klassenbezeichnung</Form.Label>
                                            <Form.Control disabled={loading || locked} isInvalid={errors.nameClass && touched.nameClass} type="text" placeholder="" name="nameClass" value={values.nameClass} onBlur={handleBlur} onChange={handleChange} />
                                            <Form.Control.Feedback type="invalid">
                                                {errors.nameClass}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Form.Group controlId="room">
                                    <Form.Check disabled={loading || locked} type="checkbox" label="Raumbezeichnung" checked={values.room} value={values.room} onChange={handleChange} />
                                </Form.Group>

                                {values.room && (
                                    <Form.Group controlId="roomName">
                                        <Form.Label>Raumbezeichnung</Form.Label>
                                        <Form.Control disabled={loading || locked} isInvalid={errors.roomName && touched.roomName} type="text" placeholder="" name="roomName" value={values.roomName} onChange={handleChange} />
                                        <Form.Control.Feedback type="invalid">
                                            {errors.roomName}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                )}

                                <Form.Group controlId="equipment">
                                    <Form.Label>Ausstattung</Form.Label>
                                    <Select isDisabled={loading || locked} isMulti name="Equipment" options={equipment} value={values.equipment} classname="basic-multi-select" placeholder="Bitte ausw&auml;hlen" onChange={
                                        (values) => {
                                            formRef.current.setFieldValue("equipment", values);
                                        }
                                    } />
                                </Form.Group>

                                <Form.Group controlId="categories">
                                    <Form.Label>NICHT erw&uuml;nschte Betriebskategorien</Form.Label>
                                    <Select isDisabled={disabled || loading || locked} isMulti name="Categories" options={categories} value={values.categories} classname="basic-multi-select" placeholder="Bitte ausw&auml;hlen" onChange={
                                        (values) => {
                                            formRef.current.setFieldValue("categories", values);
                                        }
                                    } />
                                </Form.Group>

                                <Form.Group controlId="expiration">
                                    <Form.Label>Ablaufzeit (in Stunden vor Termin-Begin)</Form.Label>
                                    <Form.Control disabled={disabled || loading || locked} isInvalid={errors.expiration && touched.expiration} type="number" step="1" value={values.expiration} onBlur={handleBlur} onChange={handleChange} />
                                    <Form.Control.Feedback type="invalid">
                                        {errors.expiration}
                                    </Form.Control.Feedback>
                                </Form.Group>

                                <Form.Group controlId="contactPerson">
                                    <Form.Label>Kontaktperson Schule</Form.Label>
                                    <Select isDisabled={loading || locked} name="contactPerson" options={contactPersons} value={values.contactPerson} classname="basic-select" onChange={
                                        (values) => {
                                            formRef.current.setFieldValue("contactPerson", values);
                                        }
                                    } />
                                </Form.Group>

                                <Form.Group controlId="contactBusiness">
                                    <Form.Label>Kontaktperson Unternehmen</Form.Label>
                                    <Select isDisabled={loading || locked} name="contactBusiness" options={businessContactPersons} value={values.contactBusiness} classname="basic-select" onChange={
                                        (values) => {
                                            formRef.current.setFieldValue("contactBusiness", values);
                                        }
                                    } />
                                </Form.Group>

                                <Form.Group controlId="sndContactBusiness">
                                    <Form.Label>Zweitkontakt Unternehmen</Form.Label>
                                    <Select isDisabled={loading || locked} name="sndContactBusiness" options={sndBusinessContactPersons} value={values.sndContactBusiness} classname="basic-select" onChange={
                                        (values) => {
                                            formRef.current.setFieldValue("sndContactBusiness", values);
                                        }
                                    } />
                                </Form.Group>
                            </Stack>
                        </Form>
                    )
                }}
            </Formik>
        </ModalDefault>
    );
};

export default TimeslotEdit;